import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';

import { Button } from '@/components/core/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuPortal,
    DropdownMenuTrigger,
} from '@/components/core/DropdownMenu/DropdownMenu';
import { Icon } from '@/components/core/Icon';
import { useTranslation } from '@/hooks/useTranslation';

import { FilterMenuItem } from '../FilterDropdown/FilterMenuItem';
import { BulkSearchDialog } from './BulkSearchDialog';
import { readStoredBulkSearch } from './bulkSearchSession';
import { BulkSearch, BulkSearchDialogState } from './bulkSearch.types';

export interface BulkSearchDropdownProps {
    onApplyBulkSearch: (bulkSearch: BulkSearch) => void;
    /** Optional seed when opened from SEARCH_QUERY_TOO_LONG. */
    dialogState?: BulkSearchDialogState | null;
    onDialogStateConsumed?: () => void;
}

export function BulkSearchDropdown({
    onApplyBulkSearch,
    dialogState,
    onDialogStateConsumed,
}: BulkSearchDropdownProps) {
    const { t } = useTranslation();
    const [menuOpen, setMenuOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [initialDialogState, setInitialDialogState] = useState<BulkSearchDialogState>({});
    const [lastBulkSearch, setLastBulkSearch] = useState(readStoredBulkSearch);

    const openNewDialog = (state: BulkSearchDialogState = {}) => {
        setInitialDialogState(state);
        setDialogOpen(true);
    };

    useEffect(() => {
        if (!dialogState) {
            return;
        }
        openNewDialog(dialogState);
        onDialogStateConsumed?.();
    }, [dialogState, onDialogStateConsumed]);

    const handleShowLastResults = () => {
        const stored = lastBulkSearch ?? readStoredBulkSearch();
        if (!stored?.id) {
            return;
        }
        onApplyBulkSearch(stored);
    };

    return (
        <>
            <DropdownMenu
                open={menuOpen}
                onOpenChange={(open) => {
                    setMenuOpen(open);
                    if (open) {
                        setLastBulkSearch(readStoredBulkSearch());
                    }
                }}
            >
                <DropdownMenuTrigger asChild>
                    <Button
                        type="button"
                        variant="secondary"
                        className="tw-h-9 tw-shrink-0"
                        aria-expanded={menuOpen}
                        aria-haspopup="menu"
                        aria-label={t('BULK_SEARCH')}
                    >
                        {t('BULK_SEARCH')}
                        <span
                            className={
                                'tw-ml-1 tw-inline-block tw-w-0 tw-h-0 tw-border-x-4 '
                                + 'tw-border-x-transparent tw-border-t-[5px] tw-border-t-current '
                                + 'tw-align-middle'
                            }
                            aria-hidden
                        />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuPortal>
                    <DropdownMenuContent
                        hasBorder
                        hasPadding={false}
                        align="start"
                        className="tw-min-w-[12rem]"
                    >
                        <FilterMenuItem onSelect={() => openNewDialog()}>
                            <span className="tw-inline-flex tw-items-center tw-gap-1.5">
                                <Icon icon={PlusIcon} size="xs" variant="secondary" />
                                {t('BULK_SEARCH.NEW')}
                            </span>
                        </FilterMenuItem>
                        {lastBulkSearch && (
                            <FilterMenuItem onSelect={handleShowLastResults}>
                                <span className="tw-inline-flex tw-items-center tw-gap-1.5">
                                    <Icon
                                        icon={MagnifyingGlassIcon}
                                        size="xs"
                                        variant="secondary"
                                    />
                                    {t('BULK_SEARCH.RESULTS')}
                                </span>
                            </FilterMenuItem>
                        )}
                    </DropdownMenuContent>
                </DropdownMenuPortal>
            </DropdownMenu>
            {dialogOpen && (
                <BulkSearchDialog
                    key={`${initialDialogState.queryTooLong ?? 0}-${initialDialogState.queryValues ?? ''}`}
                    open={dialogOpen}
                    initialState={initialDialogState}
                    onClose={() => setDialogOpen(false)}
                    onStarted={(bulkSearch) => {
                        setLastBulkSearch(bulkSearch);
                        onApplyBulkSearch(bulkSearch);
                    }}
                />
            )}
        </>
    );
}
